import L from 'leaflet';

/**
 * Shared map rendering helpers used by /map (DroneMap) and the ShopFloor mini-map.
 * Keeps marker styling, color coding, and live-detection logic in one place.
 */

// Drones currently in maintenance are displayed at the shop location
export const MAINTENANCE_LOCATION = [42.669332921043306, -83.2428363714951];

/** Generate a consistent color from a string (server name/IP). Gold for maintenance / 0-subnet. */
export function stringToColor(str) {
  if (!str) return '#888888';

  // "Maintenance by" servers get gold color
  if (str.startsWith('Maintenance by')) {
    return '#FFCC33';
  }

  // IP addresses with 3rd octet = 0 (e.g., 192.168.0.x) get gold/yellow
  const ipMatch = str.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
  if (ipMatch) {
    const thirdOctet = parseInt(ipMatch[3], 10);
    if (thirdOctet === 0) return '#FFCC33';
  }

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 55%)`;
}

/** Convert hex/hsl color to rgba/hsla with alpha for cluster ping rings. */
export function colorToRgba(color, alpha) {
  if (!color) return `rgba(136, 136, 136, ${alpha})`;
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (color.startsWith('hsl(')) {
    return color.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`);
  }
  return `rgba(136, 136, 136, ${alpha})`;
}

/** Get the effective display position for a drone (Maintenance drones use fixed shop location). */
export function getDronePosition(drone) {
  if (drone.last_seen_server?.startsWith('Maintenance by')) {
    return MAINTENANCE_LOCATION;
  }
  const [lat, lon] = drone.last_seen_location.split(',');
  return [parseFloat(lat.trim()), parseFloat(lon.trim())];
}

/** Is a drone considered "live"? Default threshold 2h; shop floor uses 1h. */
export function isLive(drone, thresholdMs = 2 * 60 * 60 * 1000) {
  if (!drone.last_seen_time) return false;
  const lastSeen = new Date(drone.last_seen_time);
  return (Date.now() - lastSeen) < thresholdMs;
}

/** Individual drone marker — colored circle with optional ping animation. */
export function createDroneIcon(color, isPinging = false) {
  const size = 12;
  const pingRings = isPinging ? `
    <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:${size}px;height:${size}px;border-radius:50%;background-color:${color};animation:ping-ring 1.5s ease-out infinite;"></div>
    <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:${size}px;height:${size}px;border-radius:50%;background-color:${color};animation:ping-ring 1.5s ease-out 0.5s infinite;"></div>
  ` : '';

  return L.divIcon({
    html: `<div style="position:relative;width:${size}px;height:${size}px;">
      ${pingRings}
      <div style="position:relative;width:${size}px;height:${size}px;background-color:${color};border:2px solid white;border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.3);z-index:1;"></div>
    </div>`,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

/** Cluster marker — sized by count, colored by dominant server, with optional ping animation. */
export function createClusterIcon(count, dominantColor, hasPinging) {
  const size = Math.min(40, Math.max(20, 14 + Math.sqrt(count) * 3));
  const clusterColorSolid = colorToRgba(dominantColor, 0.9);
  const clusterColorFaded = colorToRgba(dominantColor, 0.6);

  const pingRings = hasPinging ? `
    <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:${size}px;height:${size}px;border-radius:50%;background:${clusterColorFaded};animation:ping-ring 1.5s ease-out infinite;"></div>
    <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:${size}px;height:${size}px;border-radius:50%;background:${clusterColorFaded};animation:ping-ring 1.5s ease-out 0.5s infinite;"></div>
  ` : '';

  return L.divIcon({
    html: `<div style="position:relative;width:${size}px;height:${size}px;">
      ${pingRings}
      <div style="position:relative;width:${size}px;height:${size}px;background:${clusterColorSolid};border:2px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:${Math.max(10, size / 3)}px;box-shadow:0 2px 4px rgba(0,0,0,0.4);text-shadow:-1px -1px 0 #000,1px -1px 0 #000,-1px 1px 0 #000,1px 1px 0 #000;z-index:1;">${count}</div>
    </div>`,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}
